"use client";

import React, { useRef, useState, useMemo, useEffect } from "react";
import {
  Mail,
  Phone,
  Globe,
  Share2,
  MapPin,
  Building2,
  UserStar,
  Instagram,
  Linkedin,
  Facebook,
  Twitter,
  Music,
  Github,
  Youtube,
  Send,
  MessageCircle,
  UserRoundPlus,
  Hash,
  FileText,
} from "lucide-react";
import { Share } from "@capacitor/share";
import QRCode from "react-qr-code";

import { cn } from "../lib/utils";
import { BusinessCard } from "../../models/business-card.model";
import { Button } from "../ui/button";
import GoogleMap from "../map";
import { Label } from "../ui/label";
import { hexToRgba } from "../../utils/colorTheme";
import { SafeArea } from "capacitor-plugin-safe-area";
import { config } from "../../config";
import { Contacts } from "@capacitor-community/contacts";
import { mapBusinessCardToContactInput } from "../../utils/mapContact";
import { useToast } from "../ui/use-toast";
import { Spinner } from "@radix-ui/themes";
import { Capacitor } from "@capacitor/core";

interface BusinessCardPreviewProps {
  card: BusinessCard;
  showActions?: boolean;
  hasSafeArea?: boolean;
}

export const socialPlatforms = [
  { name: "Instagram", icon: <Instagram /> },
  { name: "LinkedIn", icon: <Linkedin /> },
  { name: "Facebook", icon: <Facebook /> },
  { name: "X (Twitter)", icon: <Twitter /> },
  { name: "TikTok", icon: <Music /> }, // placeholder
  { name: "GitHub", icon: <Github /> },
  { name: "YouTube", icon: <Youtube /> },
  { name: "Telegram", icon: <Send /> },
  { name: "WhatsApp", icon: <MessageCircle /> },
];

function getSocialIcon(platform: string) {
  return socialPlatforms.find((p) => p.name === platform)?.icon;
}

function normalizeUrl(url: string): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `https://${url}`;
}

/**
 * Renders a single-line text that auto-shrinks its font size to fit the container
 * instead of truncating with ellipsis. Wrap it in a flex parent with min-w-0.
 */
const FitText: React.FC<{
  text: string;
  href?: string;
  maxFontSize?: number;
  minFontSize?: number;
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler;
}> = ({ text, href, maxFontSize = 14, minFontSize = 8, className, style, onClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const inner = innerRef.current;
    if (!container || !inner) return;

    let size = maxFontSize;
    inner.style.fontSize = `${size}px`;

    while (inner.scrollWidth > container.clientWidth && size > minFontSize) {
      size -= 0.5;
      inner.style.fontSize = `${size}px`;
    }
  }, [text, maxFontSize, minFontSize]);

  const sharedProps = {
    ref: innerRef as any,
    className: cn("whitespace-nowrap block", className),
    style: { fontSize: maxFontSize, ...style },
    onClick,
  };

  return (
    <div ref={containerRef} className="min-w-0 flex-1 overflow-hidden">
      {href ? (
        <a {...sharedProps} href={href} target="_blank" rel="noreferrer">
          {text}
        </a>
      ) : (
        <span {...sharedProps}>{text}</span>
      )}
    </div>
  );
};

export function BusinessCardPreview({
  card,
  showActions = true,
  hasSafeArea = false,
}: BusinessCardPreviewProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isLoadingAddContact, setIsLoadingAddContact] = useState(false);
  const { toast } = useToast();

  const firstName = card.first_name || card.firstName || "";
  const lastName = card.last_name || card.lastName || "";
  const jobTitle = card.job_title || card.jobTitle || "";
  const companyName = card.company_name || card.company || "";
  const profileImage = card.profile_pic || card.profileImage;

  const theme = useMemo(() => {
    const isLightTheme = card.theme_mode === "light";
    const backgroundColor = isLightTheme ? "#f8f9fa" : "#111113";
    const textColor = isLightTheme ? "#1a1a1a" : "#f8f9fa";
    const gradientColor1 = card.primary_color || "#EA580C";
    const gradientColor2 = card.bg_value || "#F97316";

    const gradient = `linear-gradient(to right, ${gradientColor1}, ${gradientColor2})`;
    const webkitGradient = `-webkit-linear-gradient(left, ${gradientColor1}, ${gradientColor2})`;

    return {
      backgroundColor,
      textColor,
      gradient,
      webkitGradient,
      shadow: isLightTheme
        ? "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
        : "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.25)",
      primaryColor: textColor,
    };
  }, [card.theme_mode, card.primary_color, card.bg_value]);

  const {
    textColor,
    backgroundColor,
    shadow,
    gradient,
    webkitGradient,
    primaryColor,
  } = theme;
  const shareUrl = `${config.webAppUrl}/card/${card.id || ""}`;

  const cardStyle: React.CSSProperties = {
    backgroundColor,
    backgroundImage: `${webkitGradient}, ${gradient}`,
    color: textColor,
    boxShadow: shadow,
    borderRadius: "0.75rem",
    overflow: "hidden",
    ["--accent-color" as any]: primaryColor,
    WebkitBackgroundClip: "padding-box",
    WebkitAppearance: "none",
  };

  const faceCommon: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    WebkitBackfaceVisibility: "hidden",
    backfaceVisibility: "hidden",
    borderRadius: "0.75rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    pointerEvents: "auto",
  };

  const [safeArea, setSafeArea] = useState<any>({ top: 0 });
  useEffect(() => {
    if (hasSafeArea) {
      SafeArea.getSafeAreaInsets().then((data) => {
        setSafeArea(data.insets);
      });
    }
  }, []);

  const generateVCard = () => {
    const lines: string[] = [];
    lines.push("BEGIN:VCARD");
    lines.push("VERSION:3.0");
    lines.push(`FN:${firstName} ${lastName}`.trim());
    lines.push(`N:${lastName};${firstName};;;`);
    if (jobTitle) lines.push(`TITLE:${jobTitle}`);
    if (companyName) lines.push(`ORG:${companyName}`);
    if (card.email) lines.push(`EMAIL;TYPE=INTERNET:${card.email}`);
    if (card.phone) lines.push(`TEL;TYPE=CELL:${card.phone}`);
    if (card.website) lines.push(`URL:${card.website}`);
    if (card.address)
      lines.push(
        `ADR:;;${card.address.street || ""} ${
          card.address.street_number || ""
        };${card.address.city || ""};${card.address.state || ""};;;`,
      );

    if (card.secondary_emails)
      for (const e of card.secondary_emails)
        lines.push(`EMAIL;TYPE=WORK:${e.email}`);
    if (card.secondary_phones)
      for (const p of card.secondary_phones)
        lines.push(`TEL;TYPE=WORK:${p.number}`);

    lines.push("END:VCARD");
    return lines.join("\n");
  };

  const handleDownload = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    if (Capacitor.isNativePlatform()) {
      try {
        setIsLoadingAddContact(true);

        // 1. First, try to request permissions and Capture the result immediately
        console.log("Requesting permissions...");
        const requestResult = await Contacts.requestPermissions();
        let permissionValue = requestResult.contacts as string;

        console.log("Immediate permission result:", permissionValue);

        // 2. Only if the result is unclear ("prompt"), fall back to the polling check loop
        if (permissionValue === "prompt") {
          for (let attempt = 0; attempt < 3; attempt++) {
            const delay = 500 + attempt * 500;
            await new Promise((resolve) => setTimeout(resolve, delay));

            const permissionStatus = await Contacts.checkPermissions();
            permissionValue = permissionStatus.contacts as string;
            console.log(
              `Permission polling attempt ${attempt + 1}:`,
              permissionValue,
            );

            if (
              permissionValue === "granted" ||
              permissionValue === "limited"
            ) {
              break;
            }
          }
        }

        // 3. Handle Denial
        if (permissionValue === "denied") {
          setIsLoadingAddContact(false);
          toast({
            title: "Permessi negati",
            description:
              "L'accesso ai contatti è stato negato. Vai su Impostazioni > Ecard > Contatti e concedi l'accesso (anche limitato).",
            variant: "error",
          });
          return;
        }

        // 4. Proceed if Granted or Limited
        console.log(
          "Proceeding to create contact with status:",
          permissionValue,
        );

        await Promise.race([
          createNewContact(card),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("Timeout: operazione troppo lunga")),
              30000,
            ),
          ),
        ]);

        toast({
          title: "Contatto aggiunto",
          variant: "success",
        });
      } catch (e: any) {
        console.error("Error adding contact:", e);
        const errorMessage =
          e?.message ||
          e?.error?.message ||
          "Errore durante l'aggiunta del contatto";

        toast({
          title: "Errore",
          description: errorMessage,
          variant: "error",
        });
      } finally {
        setIsLoadingAddContact(false);
      }
      return;
    }

    const vcard = generateVCard();
    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${firstName || "contact"}_${lastName || ""}.vcf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    toast({
      title: "Contatto aggiunto",
      variant: "success",
    });
  };

  const handleShare = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const data = {
      title: `${firstName} ${lastName}`,
      text: `${firstName} ${lastName}`,
      url: shareUrl,
    };

    try {
      await Share.share(data);
      return;
    } catch {}

    try {
      if (navigator.share) {
        await navigator.share(data);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        alert("Link copiato negli appunti.");
      } else {
        window.prompt("Copia questo link:", shareUrl);
      }
    } catch (err) {
      console.error("share error", err);
    }
  };

  const createNewContact = async (card: BusinessCard) => {
    try {
      const contact = await mapBusinessCardToContactInput(card, shareUrl);
      console.log("Attempting to create contact...");
      const res = await Contacts.createContact({ contact });
      console.log("Contact created successfully:", res);
      return res;
    } catch (error: any) {
      console.error("Error in createNewContact:", error);
      const errorMsg =
        error?.message || error?.toString() || "Unknown error creating contact";
      throw new Error(`Failed to create contact: ${errorMsg}`);
    }
  };

  const sectionOrder = card.section_order || [
    "company",
    "address",
    "secondary",
    "social",
  ];

  const addressStreetLine = [
    card.address?.street,
    card.address?.street_number,
  ]
    .filter(Boolean)
    .join(" ");
  const addressCityLine = [card.address?.city, card.address?.state]
    .filter(Boolean)
    .join(", ");
  const addressDisplay = [addressStreetLine, addressCityLine]
    .filter(Boolean)
    .join(", ");

  const sections = [
    {
      id: "company",
      condition: !!companyName || !!card.company_logo,
      content: (
        <div
          className="rounded-xl p-6 shadow-xl backdrop-blur-xl"
          style={cardStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div>
            <div className="flex items-center space-x-2">
              <Building2 className="h-4 w-4" style={{ color: primaryColor }} />
              <span className="text-sm font-semibold">Azienda</span>
            </div>

            <div className="text-md">
              <div className="flex flex-col-reverse xs:flex-row items-start space-x-4 w-full">
                <div className="flex-1">
                  <div className="mt-1 mb-4 xs:my-6">
                    {card.company_name && (
                      <p className="font-medium text-lg ms-6 truncate">
                        {card.company_name}
                      </p>
                    )}
                    {card.company_motto && (
                      <p className="opacity-80 italic text-sm ms-6 break-words">
                        "{card.company_motto}"
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 mt-2 min-w-0">
                    {card.website && (
                      <div className="flex items-center space-x-2 text-sm min-w-0">
                        <Globe
                          className="h-4 w-4 flex-shrink-0"
                          style={{ color: primaryColor }}
                        />
                        <Label className="shrink-0">
                          <b>Sito:</b>
                        </Label>
                        <FitText
                          text={card.website}
                          href={card.website}
                          style={{ color: textColor }}
                          className="hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    {card.vat_number && (
                      <div className="flex items-center space-x-2 text-sm min-w-0">
                        <Hash
                          className="h-4 w-4 flex-shrink-0"
                          style={{ color: primaryColor }}
                        />
                        <Label className="shrink-0">
                          <b>P.IVA:</b>
                        </Label>
                        <span style={{ color: textColor }} className="min-w-0">
                          {card.vat_number}
                        </span>
                      </div>
                    )}
                    {card.sdi_code && (
                      <div className="flex items-center space-x-2 text-sm min-w-0">
                        <FileText
                          className="h-4 w-4 flex-shrink-0"
                          style={{ color: primaryColor }}
                        />
                        <Label className="shrink-0">
                          <b>SDI:</b>
                        </Label>
                        <span style={{ color: textColor }} className="min-w-0">
                          {card.sdi_code}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  {card.company_logo && (
                    <div className="flex-shrink-0 w-20 h-20 overflow-hidden rounded-md p-1 mt-2 xs-mt-2">
                      <img
                        src={card.company_logo}
                        alt="Company Logo"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src =
                            "/generic-company-logo.png";
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },

    {
      id: "address",
      condition: card.address && (card.address.street || card.address.city),
      content: (
        <div
          className="rounded-xl p-6 shadow-xl"
          style={cardStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center space-x-2 mb-2">
            <MapPin className="h-4 w-4" style={{ color: primaryColor }} />
            <span className="text-sm font-semibold">Indirizzo</span>
          </div>
          {addressDisplay && (
            <p
              className="text-sm"
              style={{
                color: textColor,
                overflowWrap: "normal",
                wordBreak: "normal",
                whiteSpace: "normal",
              }}
            >
              {addressDisplay}
            </p>
          )}
          {card.address?.street &&
            card.address?.street_number &&
            card.address?.city &&
            card.address?.state && (
              <div className="mt-3 rounded-md">
                <GoogleMap
                  address={`${card.address.street} ${card.address.street_number}, ${card.address.city}, ${card.address.state}`}
                />
              </div>
            )}
        </div>
      ),
    },

    {
      id: "secondary",
      condition:
        (card.secondary_phones && card.secondary_phones.length > 0) ||
        (card.secondary_emails && card.secondary_emails.length > 0),
      content: (
        <div className="rounded-xl p-6 shadow-xl" style={cardStyle}>
          <div className="flex items-center space-x-2 mb-2">
            <UserStar className="h-4 w-4" style={{ color: primaryColor }} />
            <span className="text-sm font-semibold">Contatti secondari</span>
          </div>
          <div className="space-y-1 mt-4">
            {card.secondary_emails?.map((em: any, i: number) => (
              <div
                key={`se-${i}`}
                className="flex items-center space-x-2 text-sm mb-2"
              >
                <Mail className="h-4 w-4" style={{ color: primaryColor }} />
                <Label>
                  <b>{em.label}</b>
                </Label>
                <a
                  style={{ color: textColor }}
                  className="hover:underline cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `tel:${em.email}`;
                  }}
                >
                  {em.email}
                </a>
              </div>
            ))}

            {card.secondary_phones?.map((ph: any, i: number) => (
              <div
                key={`sp-${i}`}
                className="flex items-center space-x-2 text-sm mb-2"
              >
                <Phone className="h-4 w-4" style={{ color: primaryColor }} />
                <Label>
                  <b>{ph.label}</b>
                </Label>
                <a
                  style={{ color: textColor }}
                  className="hover:underline cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `tel:${ph.number}`;
                  }}
                >
                  {ph.number}
                </a>
              </div>
            ))}
          </div>
        </div>
      ),
    },

    {
      id: "social",
      condition: card.socials && card.socials.length > 0,
      content: (
        <div
          className="rounded-xl p-6 shadow-xl"
          style={cardStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center space-x-2 mb-2">
            <Globe className="h-4 w-4" style={{ color: primaryColor }} />
            <span className="text-sm font-semibold">Social Media</span>
          </div>
          <div className="flex gap-3 items-center justify-center">
            {card.socials?.map((social: any, index: number) => (
              <Button
                size="iconbig"
                className="backdrop-blur-xl bg-white/5 border shadow-xl hover:opacity-75 bg-white/10"
                key={index}
                style={{
                  borderColor: primaryColor,
                  backgroundColor: hexToRgba(primaryColor, 0.2),
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <a
                  href={normalizeUrl(social.username)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xl"
                  style={{ color: primaryColor }}
                >
                  {getSocialIcon(social.platform || "") ?? (
                    <span className="text-xs">{social.platform}</span>
                  )}
                </a>
              </Button>
            ))}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div
      className="relative h-full overflow-y-auto p-4 pb-[4.25rem]"
      style={{
        background: backgroundColor,
        paddingTop: `${safeArea.top + 16}px`,
      }}
    >
      <div className="mx-auto space-y-4 w-full relative z-10">
        <div style={{ perspective: 1000 }} className="w-full mb-4">
          <div
            ref={cardRef}
            style={{
              maxWidth: "30rem",
              margin: "0 auto",
              position: "relative",
              transformStyle: "preserve-3d",
              transition: "transform 700ms",
              transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
              cursor: "pointer",
            }}
            className="relative min-h-[360px] xs:min-h-[260px]"
            onClick={() => showActions && setIsFlipped((s) => !s)}
          >
            {/* FRONT */}
            <div
              className="p-6 pb-12 xs:pb-6 shadow-2xl relative"
              style={{
                ...faceCommon,
                ...cardStyle,
                transform: "rotateY(0deg)",
              }}
            >
              {showActions && !isFlipped && (
                <div
                  className="absolute bottom-3 right-3 text-xs px-3 py-1.5 rounded-full z-20 pointer-events-none shadow-lg"
                  style={{
                    color: textColor,
                    fontWeight: "600",
                    opacity: 0.5,
                  }}
                >
                  clicca per girare
                </div>
              )}

              <div className="relative z-10 h-full flex flex-col justify-between min-w-0">
                <div className="flex items-start flex-col-reverse xs:flex-row justify-between gap-2">
                  <div className="space-y-2 min-w-0 flex-1">
                    <h3 className="text-2xl font-bold truncate">
                      {firstName || "Nome"} {lastName || "Cognome"}
                    </h3>
                    <p className="text-lg opacity-90 truncate">{jobTitle || "Ruolo"}</p>
                    {companyName && (
                      <p className="text-sm opacity-70 truncate">{companyName}</p>
                    )}
                  </div>

                  <div className="flex flex-col items-end space-y-2">
                    {profileImage && (
                      <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-current">
                        <img
                          src={profileImage}
                          alt="Profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src =
                              "/professional-headshot.png";
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  {card.email && (
                    <div className="flex items-center space-x-2 text-sm min-w-0">
                      <Mail
                        className="h-4 w-4 flex-shrink-0"
                        style={{ color: primaryColor }}
                      />
                      <a
                        style={{ color: textColor }}
                        className="hover:underline truncate"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `mailto:${card.email}`;
                        }}
                      >
                        {card.email}
                      </a>
                    </div>
                  )}
                  {card.phone && (
                    <div className="flex items-center space-x-2 text-sm min-w-0">
                      <Phone
                        className="h-4 w-4 flex-shrink-0"
                        style={{ color: primaryColor }}
                      />
                      <a
                        style={{ color: textColor }}
                        className="hover:underline truncate"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `tel:${card.phone}`;
                        }}
                      >
                        {card.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* BACK */}
            <div
              className="p-6 shadow-2xl flex items-center justify-center"
              style={{
                ...faceCommon,
                ...cardStyle,
                transform: "rotateY(180deg)",
              }}
            >
              <div className="relative z-10 w-full h-full flex flex-col xs:flex-row items-center justify-center gap-4 px-2">
                <div className="flex-shrink-0">
                  <div className="w-48 h-48 bg-white rounded-lg p-3">
                    <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                      <div onClick={(e) => e.stopPropagation()}>
                        {shareUrl ? (
                          <QRCode value={shareUrl} size={168} />
                        ) : (
                          <QRCode value={""} size={168} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-2 flex-1">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload();
                    }}
                    className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl hover:bg-white/10 w-full h-10 text-sm"
                    style={{ color: textColor }}
                  >
                    {isLoadingAddContact ? (
                      <Spinner />
                    ) : (
                      <UserRoundPlus className="h-3 w-3 mr-1" />
                    )}
                    Aggiungi
                  </Button>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare();
                    }}
                    className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl hover:bg-white/10 w-full h-10 text-sm"
                    style={{ color: textColor }}
                  >
                    <Share2 className="h-3 w-3 mr-1" /> Condividi
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 mx-auto w-full max-w-[30rem] gap-4">
          {sectionOrder
            .map((sectionId) => sections.find((s) => s.id === sectionId))
            .filter((section) => section && section.condition)
            .map((section) => (
              <div key={section!.id}>{section!.content}</div>
            ))}
        </div>
      </div>
    </div>
  );
}
